import { loanActionApiV2 } from 'app/services/api';
import _ from 'lodash';

import { LoanEditorActionsEnum } from 'constants/loanActionEditor';
import { CreditReportStatus } from 'constants/loanApplication';
import { getConditionalObject } from 'utils/data';
import {
  getMersPromptItem,
  hasMersPreviewItem,
  isMersDeactivationPromptAtExecution,
  isMersTransferPromptAtExecution,
  isSnapdocsCommentPromptAtExecution,
  isSnapdocsOptOutReasonPromptAtExecution,
} from 'utils/loanActions';
import { getPersonFullName } from 'utils/loanApplication';

import {
  creditReportServiceUnavailableMessage,
  creditReportStatusMessage,
  StatusMessageReplacerType,
} from './constants';

export const getLoanActionsAvailability = (loanActions, { isAusSummaryExist, isAusSubmitting }) =>
  loanActions.reduce(
    (acc, { loanActionTypes, id }) => {
      loanActionTypes.forEach(({ type }) => {
        if (type === LoanEditorActionsEnum.RESET_AUS_FINDINGS && !isAusSummaryExist) {
          acc.disabledLoanActionsSet.add(id);
        }

        if (type === LoanEditorActionsEnum.RUN_AUS && isAusSubmitting) {
          acc.disabledLoanActionsSet.add(id);
          acc.runningLoanActionsSet.add(id);
        }
      });

      return acc;
    },
    { disabledLoanActionsSet: new Set(), runningLoanActionsSet: new Set() },
  );

// The execute payload may carry bureausOverride ONLY when the action config allows manual
// selection AND the dialog produced form values — otherwise the server must fall back to the
// configured bureau set (LoanActionReportType.Credit.Order.ResolveBureaus).
export const getBureausOverrideMetadata = ({ withBureauSelection, values }) =>
  getConditionalObject(withBureauSelection && !!values, { bureausOverride: values?.bureaus });

const getEntitiesMessageDataByReportStatus = response =>
  response.reduce(
    (acc, { creditReportSubject: entity, status }, index, arr) => {
      const isLastElement = index === arr.length - 1;
      const isErrorOccurred = [
        CreditReportStatus.ERROR,
        CreditReportStatus.SERVER_IS_UNAVAILABLE,
      ].includes(status.latestCreditReportStatus);

      const replacerType = isErrorOccurred
        ? StatusMessageReplacerType.ERROR_FULL_NAMES
        : StatusMessageReplacerType.SUCCESS_FULL_NAMES;
      const entityFullName = getPersonFullName(entity);

      if (isErrorOccurred) {
        acc[StatusMessageReplacerType.ERROR_REASONS].push(
          status.latestCreditReportStatusDescription,
        );
      }

      acc[replacerType].push(entityFullName);

      if (isLastElement) {
        Object.values(StatusMessageReplacerType).forEach(replacerType => {
          const isErrorReasonsReplacerType =
            replacerType === StatusMessageReplacerType.ERROR_REASONS;
          const isOnlyErrorsReceived = !acc[StatusMessageReplacerType.SUCCESS_FULL_NAMES].length;

          const separator = isErrorReasonsReplacerType ? ',\n' : ', ';

          const resultedArrayData = isErrorReasonsReplacerType
            ? _.uniq(acc[replacerType]).filter(Boolean)
            : acc[replacerType];

          acc[replacerType] =
            isOnlyErrorsReceived && isErrorReasonsReplacerType
              ? resultedArrayData
              : resultedArrayData.join(separator);
        });
      }

      return acc;
    },
    {
      [StatusMessageReplacerType.SUCCESS_FULL_NAMES]: [],
      [StatusMessageReplacerType.ERROR_FULL_NAMES]: [],
      [StatusMessageReplacerType.ERROR_REASONS]: [],
    },
  );

export const getMessageOptionsOnCreditReportSubmit = ({ response, actionType }) => {
  const messageOptions = { message: '', variant: '' };

  const messageData = getEntitiesMessageDataByReportStatus(response);
  const messageReplacer = match => messageData[match];
  const messageRegExp = new RegExp(`${Object.values(StatusMessageReplacerType).join('|')}`, 'g');
  const isServerUnavailableStatus = response.every(
    ({ status }) => status.latestCreditReportStatus === CreditReportStatus.SERVER_IS_UNAVAILABLE,
  );

  const getVariant = () => {
    if (
      messageData[StatusMessageReplacerType.SUCCESS_FULL_NAMES] &&
      messageData[StatusMessageReplacerType.ERROR_FULL_NAMES]
    ) {
      return 'warning';
    }

    if (messageData[StatusMessageReplacerType.SUCCESS_FULL_NAMES]) {
      return 'success';
    }

    return 'error';
  };

  messageOptions.variant = getVariant();

  if (messageOptions.variant === 'error') {
    messageOptions.message = isServerUnavailableStatus
      ? creditReportServiceUnavailableMessage
      : messageData[StatusMessageReplacerType.ERROR_REASONS];

    return messageOptions;
  }

  if (!actionType) {
    return messageOptions;
  }

  messageOptions.message = creditReportStatusMessage[actionType][messageOptions.variant];
  messageOptions.message = messageOptions.message.replace(messageRegExp, messageReplacer);

  return messageOptions;
};

// The isNbe split is the credit-subject contract shared by the Create and Associate payload builders —
// they must partition identically or the two drift apart (FH-1450).
export const getCreditReportSubjectIds = optionsSource => ({
  borrowerIds: optionsSource.filter(({ isNbe }) => !isNbe).map(({ value }) => value),
  nonBorrowerEntityIds: optionsSource.filter(({ isNbe }) => !!isNbe).map(({ value }) => value),
});

export const executeManualLoanAction = payload => loanActionApiV2.executeLoanAction(payload);

export const getRunnerDialogIds = ({ dialogs, hostDialogIds }) =>
  dialogs.map(({ id }) => id).filter(id => !hostDialogIds.has(id));

// An action that opens its own prompt when run, or a Preview MERS run, which sends nothing, so the generic "Do you want
// to proceed?" confirmation is skipped. A Preview MERS item prompts as the MERS action it previews.
export const hasExecutionPrompt = action =>
  hasMersPreviewItem(action?.loanActionTypes) ||
  (action?.loanActionTypes ?? []).some(loanActionType => {
    const mersPromptItem = getMersPromptItem(loanActionType);

    return (
      isSnapdocsCommentPromptAtExecution(loanActionType) ||
      isSnapdocsOptOutReasonPromptAtExecution(loanActionType) ||
      isMersDeactivationPromptAtExecution(mersPromptItem) ||
      isMersTransferPromptAtExecution(mersPromptItem)
    );
  });

const HELD_FOR_FEE_REASON_TEXT = 'kept as a pending change';

export const HELD_RELOAD_DELAY_MS = 6000;

export const isHeldForFeeReason = message =>
  _.castArray(message).some(text => _.includes(text, HELD_FOR_FEE_REASON_TEXT));
